import { Button, Divider } from '@material-ui/core';
import { FieldArray, Form, Formik } from 'formik';
import React from 'react';
import styled from 'styled-components';
import ProjectWorkflowStep from './ProjectWorkflowStep';
import WorkflowFormAuthApproveOption from '../functions/WorkflowFormAutoApproveOption';
// Here is an example of a form with an editable list.
// Next to each input are buttons for insert and remove.
// If the list is empty, there is a button to add an item.


const ActionBar = styled.div`
  width:100%;
  display:flex;
  justify-content:flex-end;
  align-items:center;
  padding:20px 20px 0;
  gap:12px;
`;

const WorkflowGroupTitle = styled.div`
text-align: left;
font-size: 18px;
font-weight:500;
font-family: "Roboto", "Helvetica", "Arial", sans-serif;
padding-top:20px;
`;

const NoStepsText = styled.div`
text-align: left;
font-size: 14px;
font-weight:400;
font-family: "Roboto", "Helvetica", "Arial", sans-serif;
padding:20px;
color:gray;

`;

const ErrorInvalid = styled.div`
padding-left: 60px;
color: red;
`

const MissingPrimaryWatcher = ({ hasMissingRequiredPrimary, context }) => {
  const functionId = context.reduxStore.selectedFunction?.id;
  const setFunctionMissingPrimaryStatus = context.setFunctionMissingPrimaryStatus;

  React.useEffect(() => {
    if (
      typeof setFunctionMissingPrimaryStatus === 'function' &&
      functionId &&
      functionId !== 0
    ) {
      setFunctionMissingPrimaryStatus(functionId, hasMissingRequiredPrimary);
    }
  }, [hasMissingRequiredPrimary, setFunctionMissingPrimaryStatus, functionId]);

  return null;
};

const requiresPrimaryForStep = (workflowStep, context) => {
  const { selectedProjectOrGbu, selectedFunction } = context.reduxStore;

  if (!selectedProjectOrGbu) {
    return false;
  }

  if (selectedFunction && selectedFunction.id === 0) {
    return true;
  }

  if (selectedProjectOrGbu.isGbu) {
    return workflowStep.stepScope === "GBU" || workflowStep.stepScope === "CORP";
  }

  return workflowStep.stepScope === "PROJECT";
};

const groupWorkflowSteps = workflowSteps => {
  workflowSteps = workflowSteps.map((workflowStep, index) => ({ ...workflowStep, formIndex: index }));
  const projectSteps = workflowSteps.filter(workflowStep => workflowStep.stepScope === "PROJECT");
  const gbuSteps = workflowSteps.filter(workflowStep => workflowStep.stepScope === "GBU");
  const iwppSteps = workflowSteps.filter(workflowStep => workflowStep.stepScope === "IWPP");
  const corpSteps = workflowSteps.filter(workflowStep => workflowStep.stepScope === "CORP");

  const groupedWorkflowSteps = [

    {
      title: "Project Approvers",
      workflowSteps: projectSteps
    },
    {
      title: "GBU Function Approvers",
      workflowSteps: gbuSteps
    },
    {
      title: "Corporate Approvers",
      workflowSteps: corpSteps
    },
    {
      title: "IWPP Deviation Approvers",
      workflowSteps: iwppSteps
    },
  ];

  return groupedWorkflowSteps;
}

const renderWorkflowStepGroup = ({ workflowSteps, context, arrayHelpers, formProps }) => {


  if (workflowSteps.length) {

    return workflowSteps.map((workflowStep, index) => (
      <ProjectWorkflowStep key={index}
        isGbu={context.reduxStore.selectedProjectOrGbu.isGbu}
        editable={((workflowStep.stepScope === "GBU" || workflowStep.stepScope === "CORP") && context.reduxStore.selectedProjectOrGbu.isGbu) || (workflowStep.stepScope === "PROJECT" && !context.reduxStore.selectedProjectOrGbu.isGbu) || context.reduxStore.selectedFunction.id === 0}
        formProps={formProps}
        arrayHelpers={arrayHelpers}
        index={workflowStep.formIndex} />
    ))

  } else {
    return (
      <div>
        <NoStepsText>
          None Assigned
        </NoStepsText>
        <Divider />
      </div>
    )
  }
}


const renderWorkflowSteps = ({ workflowSteps, context, arrayHelpers, formProps }) => {

  const groupedWorkflowSteps = groupWorkflowSteps(workflowSteps);

  return groupedWorkflowSteps.map((workflowStepGroup, index) => {
    const showMissingPrimaryError = workflowStepGroup.workflowSteps.some((workflowStep) => {
      if (!requiresPrimaryForStep(workflowStep, context)) {
        return false;
      }
      return !(workflowStep.primary && workflowStep.primary.bun);
    });

    return (
      <div key={index}>
        <WorkflowGroupTitle>{workflowStepGroup.title}</WorkflowGroupTitle>
        {!!showMissingPrimaryError && <ErrorInvalid>Primary approver required for this group.</ErrorInvalid>}
        {renderWorkflowStepGroup({ workflowSteps: workflowStepGroup.workflowSteps, context, arrayHelpers, formProps })}
      </div>
    )
  });

}

const WorkflowFormContent = ({ formProps, context }) => {
  const setWorkflowPendingChanges = context.setWorkflowPendingChanges;

  React.useEffect(() => {
    if (typeof setWorkflowPendingChanges === 'function') {
      setWorkflowPendingChanges(Boolean(formProps.dirty));
    }

    return () => {
      if (typeof setWorkflowPendingChanges === 'function') {
        setWorkflowPendingChanges(false);
      }
    };
  }, [setWorkflowPendingChanges, formProps.dirty]);

  return (
    <Form>
      <FieldArray
        name="workflowSteps"
        render={arrayHelpers => {
          const isEveryPoxyHasPrimary = formProps.values.workflowSteps.filter((item) => {
            return (item.primary == null || item.primary.bun == null) && (item.proxy != null && item.proxy.length)
          }).length

          const hasMissingRequiredPrimary = formProps.values.workflowSteps.some((workflowStep) => {
            if (!requiresPrimaryForStep(workflowStep, context)) {
              return false;
            }
            return !(workflowStep.primary && workflowStep.primary.bun);
          });

          const watcherProps = {
            hasMissingRequiredPrimary,
            context,
          };

          return (
            <>
              <MissingPrimaryWatcher {...watcherProps} />
              {formProps.values.workflowSteps &&
                <>
                  <ActionBar>                    
                    <Button
                      disabled={isEveryPoxyHasPrimary || hasMissingRequiredPrimary}
                      onClick={() => {
                        if (context.reduxStore.selectedProjectOrGbu.isGbu) {
                          context.confirmGbuApproverUpdate(formProps.values, context.reduxStore.selectedProjectOrGbu.id);
                        } else {
                          context.updateProjectWorkflowApprovers(
                            formProps.values,
                            context.reduxStore.selectedProjectOrGbu.id,
                            (wasSuccessful) => {
                              formProps.setSubmitting(false);
                              if (wasSuccessful) {
                                context.showSnackBarMessage("Workflow saved successfully", null, 4000);
                              }
                            }
                          );
                        }
                      }}>Save</Button>
                      <Button disabled={!formProps.dirty && context.reduxStore.selectedProjectOrGbu.isGbu} onClick={() => {
                      formProps.resetForm();
                      context.closeWorkflowDialog();
                    }}>Cancel</Button>
                  </ActionBar>
                  {renderWorkflowSteps({ workflowSteps: formProps.values.workflowSteps, context, arrayHelpers, formProps })}
                  <WorkflowFormAuthApproveOption name={'projectShouldAutoApproveProcedures'} formProps={formProps} />
                </>
              }

            </>
          )
        }
        }
      />
    </Form>
  );
};

const ProjectWorkflowForm = ({ initialValues, context }) => (
  <Formik
    enableReinitialize
    initialValues={initialValues}
    onSubmit={values =>
      setTimeout(() => {
        alert(JSON.stringify(values, null, 2));
      }, 500)
    }
  >
    {(formProps) => (
      <WorkflowFormContent formProps={formProps} context={context} />
    )}
  </Formik>
);


export default ProjectWorkflowForm;